import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./navbar.css";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  const navLinks = [
    { id: "home", label: "หน้าหลัก", type: "route" },
    { id: "about", label: "เกี่ยวกับเรา", type: "scroll" },
    { id: "products", label: "ผลิตภัณฑ์", type: "route" },
    { id: "promotions", label: "โปรโมชั่น", type: "route" },
    { id: "portfolio", label: "ผลงาน", type: "route" },
    { id: "produce-step", label: "ขั้นตอนการสั่งทำ", type: "route" },
    { id: "contact", label: "ติดต่อเรา", type: "scroll" },
  ];

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const shouldBeScrolled = scrollTop > 50;
    
    if (shouldBeScrolled !== isScrolled) {
      setIsScrolled(shouldBeScrolled);
    }
  }, [isScrolled]);

  // Throttled scroll event
  useEffect(() => {
    let ticking = false;
    
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [handleScroll]);

  // Improved click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen && 
        menuRef.current && 
        toggleRef.current &&
        !menuRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isMobileMenuOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleNavClick = useCallback((link, e) => {
    e.preventDefault();
    
    // Close mobile menu immediately for better UX
    closeMobileMenu();
    
    if (link.type === "scroll") {
      // For scroll sections, first check if we're on homepage
      if (window.location.pathname !== '/') {
        // If not on homepage, navigate to homepage first, then scroll
        navigate('/');
        setTimeout(() => {
          scrollToSection(link.id);
        }, 300);
      } else {
        // If already on homepage, just scroll
        scrollToSection(link.id);
      }
    } else if (link.type === "route") {
      // For route navigation
      if (link.id === "home") {
        navigate('/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(`/${link.id}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [navigate, closeMobileMenu]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    
    if (element) {
      // Calculate offset to account for fixed navbar
      const navbarHeight = window.innerWidth <= 768 ? 60 : 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="navbar-logo" aria-label="3กอ อินทีเรีย Logo" />
            <div className="navbar-text">
              <h1 className="navbar-title">3กอ อินทีเรีย</h1>
              <p className="navbar-subtitle">โรงงานเฟอร์นิเจอร์ FIT-IN</p>
            </div>
          </div>

          {/* Mobile Hamburger Button */}
          <button 
            ref={toggleRef}
            className={`navbar-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="navbar-menu"
          >
            <span className="navbar-toggle-line" aria-hidden="true"></span>
            <span className="navbar-toggle-line" aria-hidden="true"></span>
            <span className="navbar-toggle-line" aria-hidden="true"></span>
          </button>

          {/* Navigation Menu */}
          <ul 
            ref={menuRef}
            id="navbar-menu"
            className={`navbar-menu ${isMobileMenuOpen ? 'mobile-open' : ''}`}
            role="menu"
          >
            {navLinks.map((link, index) => (
              <li key={link.id} className="navbar-menu-item" role="none">
                <a 
                  href={`#${link.id}`} 
                  className="navbar-link"
                  onClick={(e) => handleNavClick(link, e)}
                  role="menuitem"
                  tabIndex={isMobileMenuOpen || window.innerWidth > 768 ? 0 : -1}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="navbar-menu-overlay active"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Navbar;